import { I18n } from '@calendly/i18n';
import { FilterMenuButton } from '@calendly/ui/components/filter-menu';
import { IconAdjustments } from '@calendly/ui/components/glyphs/IconAdjustments';
import { IconChevronDown } from '@calendly/ui/components/glyphs/IconChevronDown';
import { IconChevronUp } from '@calendly/ui/components/glyphs/IconChevronUp';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { FilterMenuCountBadge, FilterMenuTriggerIcon } from 'components/cui_filter_menu';
const filterIconContainerCss = "fd2zzba";

/** Lock the 16px glyph box so flex layout cannot shrink it inside the 32px control. */
const phoneFilterIconContainerCss = "pe4apkm";

/** Phone: keep the icon centered; count badge overlays instead of sharing flex width. */
const phoneTriggerLabelCss = "peg2o49";

/**
 * NumberBadge uses withNoCssOverrides, so shrink via a wrapper transform.
 * Anchored top-right so the adjustments glyph stays readable underneath.
 */
const phoneCountBadgeOverlayCss = "p31i7d9";
const FilterIconContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(filterIconContainerCss, props.className)} />;
});
const triggerLabelGapCss = "t6h7y1m";
const TriggerLabel = forwardRef((props, ref) => <span ref={ref} {...props} className={classNames(triggerLabelGapCss, props.className)} />);
const IconContainer = ({
  filterCount,
  isModalOpen
}) => {
  if (filterCount > 0) return null;
  if (isModalOpen) return <UiIcon icon={IconChevronUp} size="small" />;
  return <UiIcon icon={IconChevronDown} size="small" />;
};
const triggerHiddenWhenOpenCss = "t18u1yke";

/**
 * Figma Filter button (3147:31356): IconButton small / secondary-outline —
 * 32×32 box, Leading icon at (8,8) size 16×16. Triple specificity beats
 * FilterMenuButton’s default height:40px / padding:8×16 so the box isn’t oversized.
 * overflow:visible lets the 16px glyph sit on the Figma 8px inset despite the 1px border.
 */
const phoneIconButtonTriggerCss = "p1cimrhe";
export const FilterButton = forwardRef(({
  isModalOpen,
  filterCount,
  hideWhenOpen = false
}, ref) => {
  const isDesktopUp = useDeviceType('isDesktopUp');
  const isPhone = useDeviceType('isPhone');
  // Figma mobile Meetings filter control uses the adjustments (sliders) glyph, not Filter2.
  const triggerIcon = isPhone ? IconAdjustments : undefined;
  return <FilterMenuButton ref={ref} hasSelection={filterCount > 0} aria-label={I18n.t('publisher.event_types.filter')} data-testid="meetings-filter-button" data-filter-trigger={isPhone ? 'icon-button-small' : undefined} disableArrow className={classNames({
    [phoneIconButtonTriggerCss]: isPhone,
    [triggerHiddenWhenOpenCss]: hideWhenOpen
  })}>
      <TriggerLabel className={isPhone ? phoneTriggerLabelCss : undefined}>
        <FilterIconContainer data-filter-icon={isPhone ? 'adjustments' : 'filter-2'} className={isPhone ? phoneFilterIconContainerCss : undefined}>
          <FilterMenuTriggerIcon selected={filterCount > 0} icon={triggerIcon} />
        </FilterIconContainer>
        {isDesktopUp && <>
            {I18n.t('publisher.event_types.filter')}
            <IconContainer filterCount={filterCount} isModalOpen={isModalOpen} />
          </>}
        {isPhone && filterCount > 0 ? <span className={phoneCountBadgeOverlayCss} data-filter-count-overlay="true">
            <FilterMenuCountBadge count={filterCount} />
          </span> : !isPhone && <FilterMenuCountBadge count={filterCount} />}
      </TriggerLabel>
    </FilterMenuButton>;
});

// Preserve legacy export name used by tests
FilterButton.displayName = 'FilterButton';

require("./filter_button.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./filter_button.js");